import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { OrderState } from '../dtos/order.dto';
import { STATUS_META, ORDER_STATES } from '../utils/orderStatus';

type Props = {
    value: OrderState;
    onChange: (state: OrderState) => void;
};

const MENU_W = 184;
const MENU_H = 196;

const OrderStateSelect = ({ value, onChange }: Props) => {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);

    const m = STATUS_META[value] ?? STATUS_META.PENDING;

    const place = () => {
        const el = triggerRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        let top = r.bottom + 6;
        if (top + MENU_H > window.innerHeight) top = r.top - MENU_H - 6;
        let left = r.right - MENU_W;
        if (left < 8) left = 8;
        setCoords({ top, left });
    };

    useLayoutEffect(() => {
        if (open) place();
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const onDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
            setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        const onMove = () => place();

        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        // capture: also catches the inner scroll container, not just window
        window.addEventListener('scroll', onMove, true);
        window.addEventListener('resize', onMove);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', onMove, true);
            window.removeEventListener('resize', onMove);
        };
    }, [open]);

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                onClick={() => setOpen(o => !o)}
                aria-haspopup="listbox"
                aria-expanded={open}
                className={`inline-flex items-center gap-2 rounded-lg border border-hair bg-canvas/40 pl-2.5 pr-2 py-1.5 text-xs font-semibold ${m.text} outline-none transition hover:bg-canvas focus-visible:ring-4 focus-visible:ring-accent/20`}
            >
                <span className={`h-2 w-2 shrink-0 rounded-full ${m.spine}`} />
                {m.label}
                <svg viewBox="0 0 24 24" className={`h-3.5 w-3.5 text-categoriesTextColor transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="m6 9 6 6 6-6" />
                </svg>
            </button>

            {open && coords && createPortal(
                <ul
                    ref={menuRef}
                    role="listbox"
                    style={{ position: 'fixed', top: coords.top, left: coords.left, width: MENU_W }}
                    className="z-[60] overflow-hidden rounded-xl border border-hair bg-surface p-1 shadow-lift animate-ticket-in"
                >
                    {ORDER_STATES.map(state => {
                        const mm = STATUS_META[state];
                        const selected = state === value;
                        return (
                            <li key={state} role="option" aria-selected={selected}>
                                <button
                                    type="button"
                                    onClick={() => { onChange(state); setOpen(false); }}
                                    className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-xs font-medium text-ink transition hover:bg-canvas ${selected ? 'bg-canvas' : ''}`}
                                >
                                    <span className={`h-2 w-2 shrink-0 rounded-full ${mm.spine}`} />
                                    <span className="flex-1 text-left">{mm.label}</span>
                                    {selected && (
                                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-accent-ink" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M20 6 9 17l-5-5" />
                                        </svg>
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ul>,
                document.body
            )}
        </>
    );
};

export default OrderStateSelect;
